import {graphql, type PreloadedQuery, usePreloadedQuery, useFragment} from 'react-relay'
import {testIdProps} from '@github-ui/test-id-props'
import type {DiscussionCategoryPickerQuery} from './__generated__/DiscussionCategoryPickerQuery.graphql'
import type {DiscussionCategoryPickerDiscussionCategories$key} from './__generated__/DiscussionCategoryPickerDiscussionCategories.graphql'
import {Select} from '@primer/react'
import {useCallback, useEffect} from 'react'

type DiscussionCategoryPickerBaseProps = {
  onSelect: (category: string) => void
}

export type DiscussionCategoryPickerProps = DiscussionCategoryPickerBaseProps & {
  categoriesQueryRef: PreloadedQuery<DiscussionCategoryPickerQuery>
}

type DiscussionCategoryPickerInternalProps = DiscussionCategoryPickerBaseProps & {
  discussionCategoriesData: DiscussionCategoryPickerDiscussionCategories$key
}

export const DiscussionCategories = graphql`
  query DiscussionCategoryPickerQuery($first: Int!, $repo: String!, $owner: String!) {
    repository(name: $repo, owner: $owner) {
      ...DiscussionCategoryPickerDiscussionCategories
    }
  }
`

export const DiscussionCategoriesFragment = graphql`
  fragment DiscussionCategoryPickerDiscussionCategories on Repository {
    discussionCategories(first: $first, filterByAssignable: true) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

export function DiscussionCategoryPicker({categoriesQueryRef, ...rest}: DiscussionCategoryPickerProps) {
  const preloadedData = usePreloadedQuery<DiscussionCategoryPickerQuery>(DiscussionCategories, categoriesQueryRef)
  return preloadedData.repository ? (
    <DiscussionCategoryPickerInternal {...rest} discussionCategoriesData={preloadedData.repository} />
  ) : null
}

export function DiscussionCategoryPickerInternal({
  discussionCategoriesData,
  onSelect,
}: DiscussionCategoryPickerInternalProps) {
  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onSelect(e.currentTarget.value)
  }

  const {discussionCategories} = useFragment(DiscussionCategoriesFragment, discussionCategoriesData)

  useEffect(() => {
    const firstCategory = discussionCategories?.edges?.[0]?.node?.id
    if (firstCategory) {
      onSelect(firstCategory)
    }
  }, [discussionCategories, onSelect])

  const renderOptions = useCallback(() => {
    if (!discussionCategories.edges) {
      return null
    }

    return discussionCategories.edges.map((category, index) => {
      if (!category || !category.node) {
        return null
      }

      return (
        <Select.Option key={index} value={category.node.id}>
          {category.node.name}
        </Select.Option>
      )
    })
  }, [discussionCategories])

  return (
    <Select block={true} onChange={onChange} {...testIdProps('select')} aria-label="Select discussion category">
      {renderOptions()}
    </Select>
  )
}

try{ DiscussionCategoryPicker.displayName ||= 'DiscussionCategoryPicker' } catch {}
try{ DiscussionCategoryPickerInternal.displayName ||= 'DiscussionCategoryPickerInternal' } catch {}